import React from "react";
import i18next from "src/i18n/config";

import { Typography } from "@mui/material";
import TimeService from "@services/timeService/timeService";
import { DateCell } from "@shared/table/DateCell";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { ColumnDef } from "@tanstack/react-table";

function getLatitudeWithSuffix(value?: number): string {
    if (!value) {
        return "-";
    }
    const suffix = value > 0 ? "N" : "S";
    return `${value}° ${suffix}`;
}
function getLongitudeWithSuffix(value?: number): string {
    if (!value) {
        return "-";
    }
    const suffix = value > 0 ? "E" : "W";
    return `${value}° ${suffix}`;
}
function getLocationTimestamp(timestamp?: number) {
    if (!timestamp) {
        return "-";
    }
    return TimeService.getHumanReadableTimestamp(timestamp);
}
function getAccuracy(accuracy?: number) {
    if (!accuracy) {
        return "-";
    }
    return accuracy + " m";
}
function sortIPs(ips: string[]): string[] {
    if (!ips || ips.length === 0) {
        return [];
    }
    return [...ips].sort((a, b) => a.localeCompare(b));
}
function getSignal(sim: { signalLevel?: number; signalStrength?: number }): string {
    const { signalLevel, signalStrength } = sim;
    if (signalLevel !== undefined && signalStrength !== undefined) {
        return `${signalLevel}/4 (${signalStrength} dBm)`;
    }
    if (signalLevel !== undefined) {
        return `${signalLevel}/4`;
    }
    if (signalStrength !== undefined) {
        return `${signalStrength} dBm`;
    }
    return "-";
}

const columns: ColumnDef<API.DeviceLocationEvent, unknown>[] = [
    {
        id: "timestamp",
        header: i18next.t("sidepanels:devicePanel.networkTab.timestamp"),
        accessorFn: (row) => row.data?.location.new.timestamp,
        enableSorting: true,
        cell: (info) => <DateCell value={info.row.original.data?.location.new.timestamp} sourceFormat="MILLIS" />,
    },
    {
        id: "latitude",
        header: i18next.t("sidepanels:devicePanel.networkTab.latitude"),
        accessorFn: (row) => row.data?.location.new.latitude,
        enableSorting: false,
        cell: (info) => {
            const latitude = info.row.original.data?.location.new.latitude;
            return (
                <MinimalTableCell>
                    <Typography>{latitude !== undefined ? getLatitudeWithSuffix(latitude) : "-"}</Typography>
                </MinimalTableCell>
            );
        },
    },
    {
        id: "longitude",
        header: i18next.t("sidepanels:devicePanel.networkTab.longitude"),
        accessorFn: (row) => row.data?.location.new.longitude,
        enableSorting: false,
        cell: (info) => {
            const longitude = info.row.original.data?.location.new.longitude;
            return (
                <MinimalTableCell>
                    <Typography>{longitude !== undefined ? getLongitudeWithSuffix(longitude) : "-"}</Typography>
                </MinimalTableCell>
            );
        },
    },
];

export default {
    getAccuracy,
    getLatitudeWithSuffix,
    getLocationTimestamp,
    getLongitudeWithSuffix,
    sortIPs,
    getSignal,
    columns,
};
